import { useEffect, useState } from 'react';
import { Plus, Save, Power, PowerOff } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

interface Plan {
  id: string;
  code: string;
  name: string;
  currency: string;
  amount_minor: number;
  interval_months: number;
  active: boolean;
}

type StatusFilter = 'all' | 'active' | 'inactive';

/** Plans are authored in the AUD base currency; other currencies derive via FX. */
const BASE_CURRENCY = 'AUD';

export function PlansPage() {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    code: '',
    name: '',
    currency: BASE_CURRENCY,
    price: '', // dollars, as typed; converted to amount_minor on submit
    interval_months: 3,
  });

  function load() {
    api<{ data: Plan[] }>('/admin/plans')
      .then((res) => setPlans(res.data))
      .catch(() => setPlans([]));
  }
  useEffect(load, []);

  async function update(id: string, patch: Partial<Plan>) {
    setBusy(id);
    setError(null);
    try {
      await api(`/admin/plans/${id}`, { method: 'PATCH', json: patch });
      load();
    } catch {
      setError('Update failed — check the values are valid.');
    } finally {
      setBusy(null);
    }
  }

  async function setActive(plan: Plan, active: boolean) {
    if (
      !active &&
      !window.confirm('Deactivate this plan? Existing subscriptions keep referencing it.')
    ) {
      return;
    }
    setBusy(plan.id);
    setError(null);
    try {
      // A dedicated deactivate endpoint exists for the audit trail; reactivating
      // is a plain PATCH of the `active` flag.
      if (active) {
        await api(`/admin/plans/${plan.id}`, { method: 'PATCH', json: { active: true } });
      } else {
        await api(`/admin/plans/${plan.id}/deactivate`, { method: 'POST' });
      }
      load();
    } catch {
      setError('Action failed.');
    } finally {
      setBusy(null);
    }
  }

  async function create() {
    setError(null);
    try {
      await api('/admin/plans', {
        method: 'POST',
        json: {
          code: draft.code,
          name: draft.name,
          currency: draft.currency,
          amount_minor: dollarsToMinor(draft.price),
          interval_months: draft.interval_months,
        },
      });
      setDraft({
        code: '',
        name: '',
        currency: BASE_CURRENCY,
        price: '',
        interval_months: 3,
      });
      load();
    } catch {
      setError('Create failed — check the code is unique and fields are valid.');
    }
  }

  const visible = plans?.filter((p) => filter === 'all' || (filter === 'active') === p.active);
  const inactiveCount = plans?.filter((p) => !p.active).length ?? 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight">Plans & pricing</h1>
        <p className="text-sm text-muted-foreground">
          Create, edit price &amp; duration, and activate/deactivate plans. Prices are in the{' '}
          {BASE_CURRENCY} base currency; <code className="font-mono text-xs">code</code> is
          immutable.
        </p>
      </header>

      {error !== null && (
        <p
          role="alert"
          className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>Existing plans</CardTitle>
          <div role="group" aria-label="Filter by status" className="flex gap-1">
            {(['all', 'active', 'inactive'] as const).map((f) => (
              <Button
                key={f}
                type="button"
                size="sm"
                variant={filter === f ? 'default' : 'outline'}
                onClick={() => setFilter(f)}
              >
                {f}
                {f === 'inactive' && inactiveCount > 0 ? ` (${inactiveCount})` : ''}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {plans === null && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
          {visible !== undefined && visible.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">No plans in this view.</p>
          )}
          {visible !== undefined && visible.length > 0 && (
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-2 font-bold">Code</th>
                  <th className="py-2 font-bold">Name</th>
                  <th className="py-2 font-bold">Price (AUD)</th>
                  <th className="py-2 font-bold">Months</th>
                  <th className="py-2 font-bold">Status</th>
                  <th className="py-2 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visible.map((p) => (
                  <PlanRow
                    key={p.id}
                    plan={p}
                    busy={busy === p.id}
                    onSave={(patch) => void update(p.id, patch)}
                    onToggleActive={(active) => void setActive(p, active)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create a new plan</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
            onSubmit={(e) => {
              e.preventDefault();
              void create();
            }}
          >
            <Field
              label="Code"
              value={draft.code}
              onChange={(v) => setDraft({ ...draft, code: v })}
            />
            <Field
              label="Name"
              value={draft.name}
              onChange={(v) => setDraft({ ...draft, name: v })}
            />
            <Field
              label="Currency"
              value={draft.currency}
              onChange={(v) => setDraft({ ...draft, currency: v.toUpperCase() })}
            />
            <Field
              label="Price (AUD)"
              type="number"
              min="0"
              step="0.01"
              value={draft.price}
              onChange={(v) => setDraft({ ...draft, price: v })}
            />
            <Field
              label="Months"
              type="number"
              value={String(draft.interval_months)}
              onChange={(v) => setDraft({ ...draft, interval_months: Number(v) || 1 })}
            />
            <div className="flex items-center gap-3 lg:col-span-5">
              <Button type="submit" disabled={draft.code === '' || draft.name === ''}>
                <Plus className="mr-2 h-4 w-4" /> Create plan
              </Button>
              <span className="text-xs text-muted-foreground">
                {dollarsToMinor(draft.price) > 0
                  ? `= ${formatPrice(dollarsToMinor(draft.price), draft.currency)}`
                  : 'Enter the price in dollars, e.g. 24.99.'}
              </span>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function PlanRow({
  plan,
  busy,
  onSave,
  onToggleActive,
}: {
  plan: Plan;
  busy: boolean;
  onSave: (patch: Partial<Plan>) => void;
  onToggleActive: (active: boolean) => void;
}) {
  const [name, setName] = useState(plan.name);
  const [price, setPrice] = useState(minorToDollars(plan.amount_minor));
  const [months, setMonths] = useState(plan.interval_months);
  const amountMinor = dollarsToMinor(price);
  const dirty =
    name !== plan.name || amountMinor !== plan.amount_minor || months !== plan.interval_months;

  return (
    <tr className={plan.active ? '' : 'opacity-70'}>
      <td className="py-3">
        <code className="font-mono text-xs">{plan.code}</code>
      </td>
      <td className="py-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs" />
      </td>
      <td className="py-3">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="max-w-[120px]"
          />
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {formatPrice(amountMinor, plan.currency)}
          </span>
        </div>
      </td>
      <td className="py-3">
        <Input
          type="number"
          min={1}
          max={36}
          value={months}
          onChange={(e) => setMonths(Number(e.target.value) || 1)}
          className="max-w-[80px]"
        />
      </td>
      <td className="py-3">
        <Badge variant={plan.active ? 'default' : 'secondary'}>
          {plan.active ? 'active' : 'inactive'}
        </Badge>
      </td>
      <td className="py-3">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            disabled={!dirty || busy}
            onClick={() => onSave({ name, amount_minor: amountMinor, interval_months: months })}
          >
            <Save className="mr-1 h-4 w-4" /> Save
          </Button>
          {plan.active ? (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => onToggleActive(false)}
            >
              <PowerOff className="mr-1 h-4 w-4" /> Deactivate
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => onToggleActive(true)}
            >
              <Power className="mr-1 h-4 w-4" /> Activate
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  min,
  step,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  min?: string;
  step?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type={type}
        min={min}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** Parse a dollar string (e.g. "24.99") into integer minor units (2499). */
function dollarsToMinor(dollars: string): number {
  const n = Number.parseFloat(dollars);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/** Render minor units (2499) as an editable dollar string ("24.99"). */
function minorToDollars(amountMinor: number): string {
  return (amountMinor / 100).toFixed(2);
}

function formatPrice(amountMinor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(
      amountMinor / 100,
    );
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency}`;
  }
}
